import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { syncHubBookmarks, setMemberFlag, hasMemberFlag } from "@/utils/hub";
import { devError } from "@/utils/logger";
import { clearLegacyAuthStorage } from "@/utils/preferences";
import { onSessionLost, refreshSession } from "@/api/session";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

async function probeSession() {
  const { data } = await axios.get(`${API}/auth/session`);
  if (data.authenticated || !hasMemberFlag()) return data;
  // Access cookie expired but the refresh cookie may still be valid — renew once before giving up.
  await refreshSession();
  return (await axios.get(`${API}/auth/session`)).data;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in

  useEffect(() => {
    clearLegacyAuthStorage();
    onSessionLost(() => { setUser(false); setMemberFlag(false); });
    probeSession()
      .then((data) => {
        const sessionUser = data.authenticated ? data.user : false;
        setUser(sessionUser);
        setMemberFlag(Boolean(sessionUser));
        if (sessionUser) syncHubBookmarks();
      })
      .catch((error) => {
        if (error.response?.status !== 401) devError("Session check failed:", error);
        setUser(false);
        setMemberFlag(false);
      });
    return () => onSessionLost(null);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    setMemberFlag(true);
    syncHubBookmarks();
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
    setUser(data);
    setMemberFlag(true);
    syncHubBookmarks();
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (error) { devError("Server logout failed; local session state was cleared:", error); }
    setUser(false);
    setMemberFlag(false);
  }, []);

  const value = useMemo(() => ({ user, login, register, logout }), [user, login, register, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
